import { api, config, helpers, ts } from '_core';

import { generateSignalsFromVisual } from './definition';

type SignalSetApiResponse = {
  data: ts.types.signalSet.SignalSet;
};

export const createSignalSet = async (
  newData: ts.types.signalSet.SignalSetDraft,
  callback: (_data: ts.types.signalSet.SignalSet) => void,
  errorCallback: (_error: string) => void
) => {
  try {
    const resp: SignalSetApiResponse = await api.signal_sets.create(newData);
    callback(resp.data);
  } catch (error) {
    errorCallback(`Error creating signal set - ${helpers.parseApiError(error as ts.types.common.ApiError)}`);
  }
};

export const updateSignalSet = async (
  id: number,
  newData: ts.types.signalSet.SignalSetDraft,
  callback: (_data: ts.types.signalSet.SignalSet) => void,
  errorCallback: (_error: string) => void
) => {
  try {
    const resp: SignalSetApiResponse = await api.signal_sets.update(id, { ...newData });
    callback(resp.data);
  } catch (error) {
    errorCallback(`Error updating signal set - ${helpers.parseApiError(error as ts.types.common.ApiError)}`);
  }
};

export const keepCurrent = async (
  signalSet: ts.types.signalSet.SignalSetDraft,
  callback: (_data: { resource: ts.types.signalSet.SignalSet; universe?: ts.types.universe.Universe }) => void,
  errorCallback: (_error: string) => void
) => {
  try {
    if (signalSet.shared) return;
    const resp = await api.dates.keep_current({
      resource_id: signalSet.id,
      resource_type: 'signal_sets',
      frequency: signalSet?.ui_metadata?.visual_def?.frequency,
      // Until we don't have a universe, our keep current will be just the max date we can have
      universe_id: signalSet?.ui_metadata?.visual_def?.universe_id || undefined,
      max_date: config.features.end_date,
    });
    callback(resp);
  } catch (error) {
    errorCallback(`Error updating signal set dates - ${helpers.parseApiError(error as ts.types.common.ApiError)}`);
  }
};

export const updateDefinition = async (
  signalSet: ts.types.signalSet.SignalSet,
  definition: ts.types.signalSet.DefinitionDraft,
  isValid: boolean,
  callback: (_data: ts.types.signalSet.SignalSet, _signals: ts.types.signalSet.SignalMetadata[]) => void,
  errorCallback: (_error: string) => void
) => {
  try {
    const signals = generateSignalsFromVisual(definition as ts.types.signalSet.Definition);

    const resp = await api.signal_sets.update(
      signalSet.id,
      {
        definition: {
          signals,
        },
        ui_metadata: {
          ...signalSet.ui_metadata,
          visual_def: definition,
        },
        is_valid: isValid,
        skip_signature_check: !isValid,
      },
      true
    );

    callback(resp.data, signals);
  } catch (error) {
    errorCallback(`Error updating the definition - ${helpers.parseApiError(error as ts.types.common.ApiError)}`);
  }
};

export const updateUiMetadata = async (
  signalSet: ts.types.signalSet.SignalSet,
  uiMetaData: ts.types.signalSet.UiMetaData,
  callback: (_data: ts.types.signalSet.SignalSet) => void,
  errorCallback: (_error: string) => void
) => {
  try {
    if (signalSet.shared) return;
    const updateData = {
      ui_metadata: uiMetaData,
      skip_signature_check: true,
    } as ts.types.signalSet.SignalSetDraft;
    const resp = await api.signal_sets.update(signalSet.id, updateData);
    callback(resp.data);
  } catch (error) {
    errorCallback(`Error updating the resource metadata - ${helpers.parseApiError(error as ts.types.common.ApiError)}`);
  }
};
